import { Auth } from "aws-amplify";
import { Component, FormEvent, FunctionComponent } from "react";
import { Form, Alert, Card, Row, Col } from "react-bootstrap";
import { InfoCircle } from "react-bootstrap-icons";
import { RouteComponentProps } from "react-router-dom";
import { ContactDetails, ISetSocialMedia, SocialMedia } from "../../../client/core";
import { Color, Path, Storage } from "../../../env";
import { Button, icon, variant } from "../../form/Button";

export interface SetSetSocialMediaState {
    error: string;
    isLoading: boolean;
    isFacebookValid: boolean;
    isInstagramValid: boolean;
    isWhatsAppValid: boolean;
    whatsapp: string;
    instagram: string;
    facebook: string;
}

export interface SetSocialMediaProps extends RouteComponentProps {
    setSocialMediaAPI: ISetSocialMedia;
    auth: typeof Auth;
}

export default class SetSocialMedia extends Component<
    SetSocialMediaProps,
    SetSetSocialMediaState
> {
    constructor(props: SetSocialMediaProps) {
        super(props);
        this.state = {
            isLoading: false,
            isFacebookValid: false,
            isInstagramValid: false,
            isWhatsAppValid: false,
            error: "",
            facebook: "",
            instagram: "",
            whatsapp: "",
        };
    }

    handleSubmit = () => {
        this.setState({
            isLoading: true
        })

        if (!this.state.isInstagramValid && this.state.instagram.trim() !== "") {
            return this.setState({
                isLoading: false,
                error: "Oops! Your Instagram link/username seems off — double-check and enter it again."
            })
        }

        if (!this.state.isFacebookValid && this.state.facebook.trim() !== "") {
            return this.setState({
                isLoading: false,
                error: "Oops! Your Facebook link seems off — try copying the URL directly from your Facebook page."
            })
        }

        if (!this.state.isWhatsAppValid && this.state.whatsapp.trim() !== "") {
            return this.setState({
                isLoading: false,
                error: "Oops! Your WhatsApp number seems off — double-check and enter it again."
            })
        }

        const shopID = localStorage.getItem(Storage.ShopID);
        if (shopID == null) {
            localStorage.removeItem(Storage.ShopID);
            this.props.auth.signOut();
            return this.props.history.push(Path.Login)
        }

        this.props.auth.currentSession().then((session) => {
            const token = session.getIdToken().getJwtToken();
            this.props.setSocialMediaAPI
                .SetSocialMedia({
                    identityToken: token,
                    shopId: shopID,
                    socialMedia: {
                        facebook: this.state.facebook,
                        instagram: this.state.instagram,
                        whatsapp: this.state.whatsapp
                    },
                })
                .then((output) => {
                    if (output.statusCode === 403) {
                        localStorage.removeItem(Storage.ShopID);
                        this.props.auth.signOut();
                        return this.props.history.push(Path.Login)
                    }

                    if (output.statusCode != 204) {
                        return this.setState({
                            error: "Please double check the social media entered. It appears at least one of the fields is invalid.",
                            isLoading: false,
                        })
                    }

                    return this.props.history.push(Path.RegisterShopAddress);
                })
                .catch(() =>
                    this.setState({
                        error: "We were unable to set your contact details at this time.",
                        isLoading: false,
                    })
                );
        }).catch(() => {
            localStorage.removeItem(Storage.ShopID);
            this.props.auth.signOut();
            return this.props.history.push(Path.Login)
        })
    };

    onInstagramChange = (value: string) => {
        const regex = /^(?:https?:\/\/(?:www\.)?instagram\.com\/[A-Za-z0-9._]{1,30}\/?|[A-Za-z0-9._]{1,30})$/;
        const isValid = regex.test(value);
        console.log(value)
        console.log(isValid)
        this.setState({
            instagram: value,
            isInstagramValid: isValid
        });
    }

    onFacebookChange = (value: string) => {
        console.log("test")
        const regex = /facebook\.com\/[A-Za-z0-9._-]+/i;
        const isValid = regex.test(value);
        this.setState({
            facebook: value,
            isFacebookValid: isValid
        });
    }

    onWhatsAppChange = (value: string) => {
        const regex = /^(0\d{10}|44\d{10}|\+44\d{10})$/;
        const isValid = regex.test(value);
        this.setState({
            whatsapp: value,
            isWhatsAppValid: isValid,
        });
    }

    render() {
        const props: SetSetSocialMediaFormProps = {
            isLoading: this.state.isLoading,
            error: this.state.error,
            isFacebookValid: this.state.isFacebookValid,
            isInstagramValid: this.state.isInstagramValid,
            isWhatsAppValid: this.state.isWhatsAppValid,
            facebook: this.state.facebook,
            instagram: this.state.instagram,
            whatsapp: this.state.whatsapp,
            onSubmit: this.handleSubmit,
            onSkip: () => this.props.history.push(Path.RegisterShopAddress),
            onFacebookChange: this.onFacebookChange,
            onInstagramChange: this.onInstagramChange,
            onWhatsAppChange: this.onWhatsAppChange
        };
        return <SetSetSocialMediaForm {...props} />;
    }
}

export interface SetSetSocialMediaFormProps {
    error: string;
    isFacebookValid: boolean;
    isInstagramValid: boolean;
    isWhatsAppValid: boolean;
    facebook: string;
    instagram: string;
    whatsapp: string;
    isLoading: boolean;
    onSkip: () => void;
    onSubmit: () => void;
    onFacebookChange: (value: string) => void
    onInstagramChange: (value: string) => void
    onWhatsAppChange: (value: string) => void
}

export const SetSetSocialMediaForm: FunctionComponent<SetSetSocialMediaFormProps> = (
    props
) => (
    <Form>
        <h1 style={{ marginBottom: "2rem" }}>Link Social Media</h1>
        <Alert variant={"danger"} show={props.error != ""}>
            {props.error}
        </Alert>
        <Form.Group style={{ "width": "100%" }}>
            <Form.Group className="mb-3" controlId="formEmail">
                <Form.Label>Instagram <span style={{ fontSize: "0.85rem", color: "grey" }}>(optional)</span></Form.Label>
                <Form.Control isValid={props.isInstagramValid} type="text" placeholder="Enter page URL or Username e.g. @username" onChange={(e: any) => props.onInstagramChange(e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formPhone">
                <Form.Label>Facebook <span style={{ fontSize: "0.85rem", color: "grey" }}>(optional)</span></Form.Label>
                <Form.Control isValid={props.isFacebookValid} type="text" placeholder="Enter the page URL" onChange={(e: any) => props.onFacebookChange(e.target.value)} />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formWhatsapp">
                <Form.Label>WhatsApp <span style={{ fontSize: "0.85rem", color: "grey" }}>(optional)</span></Form.Label>
                <Form.Control isValid={props.isWhatsAppValid} type="text" placeholder="Enter your WhatsApp number" onChange={(e: any) => props.onWhatsAppChange(e.target.value)} />
            </Form.Group>
            <Button
                id={"set-contact-details-btn"}
                icon={(props.isLoading) ? icon.Spinner : undefined}
                buttonvariant={variant.Primary}
                disabled={props.isLoading}
                style={{ width: "100%", marginTop: "2rem" }}
                name={"Continue"}
                onClick={() => props.onSubmit()}
            />
            <Button
                id={"skip-contact-details-btn"}
                buttonvariant={variant.Secondary}
                style={{ width: "100%", marginTop: "1rem" }}
                name={"Skip"}
                onClick={() => props.onSkip()}
            />
        </Form.Group>
    </Form >
);